import { useState } from 'react'

import { Button, Modal, Trans } from '@/components'

import type { MemberTarget } from './AssignCreditDialog'

type Props = {
  open: boolean
  variant: 'bulk' | 'single'
  targets: MemberTarget[]
  perMemberAmount: number
  totalCost: number
  onClose: () => void
  onConfirm: () => Promise<void> | void
}

function formatCredits(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function ConfirmAssignmentDialog({
  open,
  variant,
  targets,
  perMemberAmount,
  totalCost,
  onClose,
  onConfirm,
}: Props) {
  const [loading, setLoading] = useState(false)

  const single = targets[0]
  const afterBalance = single ? (single.currentCredit ?? 0) / 100 + perMemberAmount : 0

  async function handleConfirm() {
    setLoading(true)
    try {
      await onConfirm()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[480px]">
      <Modal.Header>
        {variant === 'bulk' ? (
          <Trans i18nKey="team.member.dialog.confirmBulkAssignment">Confirm Bulk Assignment</Trans>
        ) : (
          <Trans i18nKey="team.member.dialog.confirmCreditAssignment">
            Confirm Credit Assignment
          </Trans>
        )}
      </Modal.Header>

      <Modal.Body>
        {variant === 'bulk' ? (
          <>
            {/* Description */}
            <p className="text-secondary mb-4 t-sm">
              <Trans
                i18nKey="team.member.dialog.assigningCreditsToMembers"
                values={{ credits: formatCredits(perMemberAmount), count: targets.length }}
                components={{
                  1: <span className="text-primary" />,
                  3: <span className="text-primary" />,
                }}
              />
            </p>

            {/* Table */}
            <div className={targets.length > 5 ? 'max-h-[192px] overflow-y-auto' : ''}>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200 t-xs">
                    <th className="text-left py-2 px-3">
                      <Trans i18nKey="team.member.dialog.member">Member</Trans>
                    </th>
                    <th className="text-left py-2 px-3">
                      <Trans i18nKey="team.member.dialog.name">Name</Trans>
                    </th>
                    <th className="text-right py-2 px-3">
                      <Trans i18nKey="team.member.dialog.credits">Credits</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {targets.map((target, idx) => (
                    <tr key={target.id} className={idx % 2 === 0 ? 'bg-neutral-50' : ''}>
                      <td className="py-3 px-3 t-xs text-primary">{target.name}</td>
                      <td className="py-3 px-3 t-xs text-highlight">{target.name}</td>
                      <td className="py-3 px-3 t-xs text-primary text-right">
                        {formatCredits(perMemberAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Cost */}
            <div className="mt-4 text-secondary">
              <Trans i18nKey="team.member.dialog.totalCost">Total Cost:</Trans>{' '}
              <span className="font-semibold text-primary">
                {formatCredits(totalCost)}{' '}
                <Trans i18nKey="team.member.dialog.credits">Credits</Trans>
              </span>
            </div>
          </>
        ) : (
          /* Single mode */
          <p className="text-secondary t-sm">
            <Trans
              i18nKey="team.member.dialog.confirmAssignCreditsTo"
              values={{
                credits: formatCredits(perMemberAmount),
                name: single?.name,
                balance: formatCredits(afterBalance),
              }}
            >
              Are you sure you want to assign{' '}
              <span className="font-semibold text-primary">
                {formatCredits(perMemberAmount)} Credits
              </span>{' '}
              to <span className="text-highlight">{single?.name}</span>? This will increase their
              balance to{' '}
              <span className="font-semibold text-primary">
                {formatCredits(afterBalance)} Credits
              </span>
              .
            </Trans>
          </p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button type="outline" className="min-w-[88px]" onClick={onClose} disabled={loading}>
          <Trans i18nKey="common.cancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          className="min-w-[145px]"
          onClick={handleConfirm}
          disabled={loading}
        >
          <Trans i18nKey="team.member.dialog.confirmAssignment">Confirm Assignment</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
